import { BundleType, TRIAL_TITLE } from '@peach/types/bundles/types.js';
import type { BundleDto } from '@peach/types/bundles/bundle.dto.js';
import { oldPrice, startingPrice } from './pricing';

export { BundleType } from '@peach/types/bundles/types.js';

const DURATION_TRIAL = 15;
const DURATION_ONE_ON_ONE = 50;

/** Caught at the keystroke it is a full field; caught at the request it is an
 *  error nobody can act on. The card's own measure is what sets it. */
export const BUNDLE_TITLE_MAX = 60;

/**
 * One product as the owner is editing it — everything the save needs and
 * nothing derived. Formatted prices used to live here too; they were a second
 * copy of `price` that could disagree with it, so the view formats its own.
 */
export interface BundleDraft {
  id: string;
  type: BundleType;
  numberOfClasses: number;
  duration: number;
  price: number;
  /** The struck price a deal shows; set from the price, never typed. */
  originalPrice: number | undefined;
  /** "Make it a deal": show an old price, struck, above the price. */
  deal: boolean;
  title: string;
  description: string;
}

function getDuration(type: BundleType): number {
  if (type === BundleType.TRIAL) return DURATION_TRIAL;
  return DURATION_ONE_ON_ONE;
}

/**
 * The name a product carries until its owner gives it one, said from its terms,
 * so it stays true as they change: "50-minute call", "One-hour call", "4 calls".
 */
export function getDefaultTitle(
  type: BundleType,
  numberOfClasses: number,
  duration: number
): string {
  if (type === BundleType.TRIAL) return TRIAL_TITLE;
  if (numberOfClasses > 1) return `${numberOfClasses} calls`;
  return duration === 60 ? 'One-hour call' : `${duration}-minute call`;
}

/** A title its owner typed: not empty, and not the one the terms gave it. */
export function hasOwnTitle(
  b: Pick<BundleDraft, 'type' | 'numberOfClasses' | 'duration' | 'title'>
) {
  return !!b.title && b.title !== getDefaultTitle(b.type, b.numberOfClasses, b.duration);
}

function generateBundleId(): string {
  return `new-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * A brand-new product is born complete: priced at `rate` under its starting
 * discount, with a legal length. There is no half-written state to hide from
 * buyers, which is why the card goes straight onto the page and is edited there.
 */
export function newBundle(type: BundleType, numberOfClasses: number, rate: number): BundleDraft {
  const duration = getDuration(type);
  const price = startingPrice({ type, numberOfClasses, duration }, rate);
  const deal = numberOfClasses > 1 || type === BundleType.TRIAL;
  return {
    id: generateBundleId(),
    type,
    numberOfClasses,
    duration,
    price,
    originalPrice: deal ? oldPrice(price) : undefined,
    deal,
    title: getDefaultTitle(type, numberOfClasses, duration),
    description: '',
  };
}

export function transformBundles(bundles: BundleDto[]): BundleDraft[] {
  return bundles.map((bundle) => {
    const type = (bundle.type as BundleType) || BundleType.ONE_ON_ONE;
    return {
      id: bundle.id,
      type,
      numberOfClasses: type === BundleType.TRIAL ? 1 : bundle.numberOfClasses,
      duration: bundle.duration || getDuration(type),
      price: bundle.price,
      originalPrice: bundle.previousPrice ?? undefined,
      deal: (bundle.previousPrice ?? 0) > bundle.price,
      title: bundle.title || '',
      description: bundle.description || '',
    };
  });
}
